import {
    useMutation as apolloUseMutation,
    useLazyQuery as apolloUseLazyQuery,
    useQuery as apolloUseQuery,
    useSuspenseQuery,
    skipToken,
    type LazyQueryHookExecOptions,
    type LazyQueryExecFunction,
    type LazyQueryHookOptions,
    type LazyQueryResultTuple,
    type MutationHookOptions,
    type MutationTuple,
    type MutationResult,
    type MutationFunctionOptions,
    type QueryHookOptions,
    type QueryResult,
    type SuspenseQueryHookOptions,
    type SkipToken,
} from '@apollo/client/react';
import type { DocumentNode, OperationVariables, ApolloCache, DefaultContext } from '@apollo/client';
import { useAnalyticsContext } from 'src/lib/analytics/context';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';

// reexport the whole module, then override specific functions
export * from '@apollo/client';

// re-export helpers that generated code expects from the patched module
export { useSuspenseQuery, skipToken } from '@apollo/client/react';

export type {
    QueryHookOptions,
    QueryResult,
    LazyQueryHookOptions,
    LazyQueryResultTuple,
    MutationHookOptions,
    MutationTuple,
    MutationResult,
    MutationFunctionOptions,
    LazyQueryHookExecOptions,
    LazyQueryExecFunction,
    SuspenseQueryHookOptions,
    SkipToken,
} from '@apollo/client/react';

export type MutationFunction<
    TData = any,
    TVariables extends OperationVariables = OperationVariables,
> = (options?: MutationFunctionOptions<TData, TVariables>) => Promise<MutationResult<TData>>;

export type BaseMutationOptions<
    TData = any,
    TVariables extends OperationVariables = OperationVariables,
    TContext = DefaultContext,
    TCache extends ApolloCache = ApolloCache,
> = MutationHookOptions<TData, TVariables, TContext, TCache>;

type AnyVariables = Record<string, unknown>;

function withAnalyticsHeaders<TOptions>(options: TOptions | undefined): TOptions | undefined {
    const context = useAnalyticsContext();
    if (!options) {
        return options;
    }

    type HasContext = { context?: DefaultContext };
    const existingContext = (options as HasContext).context;

    return {
        ...options,
        context: {
            ...existingContext,
            headers: {
                ...existingContext?.headers,
                ['X-ProductWorkflow']: context?.context?.productWorkflow?.toString() || undefined,
                ['X-Webpage']: context?.context?.pageName?.toString() || undefined,
            },
        },
    } as TOptions;
}

export function useQuery<TData = any, TVariables extends OperationVariables = OperationVariables>(
    query: DocumentNode | TypedDocumentNode<TData, TVariables>,
    options?: QueryHookOptions<TData, TVariables>,
): QueryResult<TData, TVariables> {
    const annotatedOptions = withAnalyticsHeaders(options);
    return apolloUseQuery<TData, TVariables>(query, annotatedOptions as any);
}

export function useLazyQuery<
    TData = any,
    TVariables extends OperationVariables = OperationVariables,
>(
    query: DocumentNode | TypedDocumentNode<TData, TVariables>,
    options?: LazyQueryHookOptions<TData, TVariables>,
): LazyQueryResultTuple<TData, TVariables> {
    const annotatedOptions = withAnalyticsHeaders(options);
    return apolloUseLazyQuery<TData, TVariables>(query, annotatedOptions as any);
}

export function useMutation<
    TData = any,
    TVariables extends OperationVariables = OperationVariables,
    TContext = DefaultContext,
    TCache extends ApolloCache = ApolloCache,
>(
    mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
    options?: MutationHookOptions<TData, TVariables, TContext, TCache>,
): MutationTuple<TData, TVariables, TContext, TCache> {
    const annotatedOptions = withAnalyticsHeaders(options);
    return apolloUseMutation(mutation, annotatedOptions as any) as MutationTuple<
        TData,
        TVariables,
        TContext,
        TCache
    >;
}

export function useTypedQuery<TData, TVariables extends AnyVariables = AnyVariables>(
    document: TypedDocumentNode<TData, TVariables>,
    options?: QueryHookOptions<TData, TVariables>,
): QueryResult<TData, TVariables> {
    return useQuery<TData, TVariables>(document, options);
}

export function useTypedLazyQuery<TData, TVariables extends AnyVariables = AnyVariables>(
    document: TypedDocumentNode<TData, TVariables>,
    options?: LazyQueryHookOptions<TData, TVariables>,
): LazyQueryResultTuple<TData, TVariables> {
    return useLazyQuery<TData, TVariables>(document, options);
}

export function useTypedMutation<
    TData,
    TVariables extends AnyVariables = AnyVariables,
    TContext = DefaultContext,
    TCache extends ApolloCache = ApolloCache,
>(
    document: TypedDocumentNode<TData, TVariables>,
    options?: MutationHookOptions<TData, TVariables, TContext, TCache>,
): MutationTuple<TData, TVariables, TContext, TCache> {
    return useMutation<TData, TVariables, TContext, TCache>(document, options);
}
